import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MessageCircle } from "lucide-react";
import { Box, IconButton, Button, alpha } from "@mui/material";
import ChartBoxCode from "./ChartBoxCode";
import { getUserProfile, deriveCustomerNameFromEmail } from "../../utils/auth";
import { ASSISTANT_NAME } from "../../config/assistantConfig";
import { useAuth } from "../../context/AuthContext";
import OrbieeFlow from "./OrbieeFlow";
import {
  buildChatBubbleTextsFromResponse,
  subscribeOrbieeBackendWelcome,
  getOrbieeBackendWelcomeMessage,
} from "../../services/websocket";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { applyWishlistFromAgentResponse } from "../../utils/applyWishlistFromAgentResponse";
import { applyCartFromAgentResponse } from "../../utils/agentCartPayload";
import { startVoiceRecording, convertBuffersToWAV } from "./AudioCode";
import {
  isValidTranscription,
  isEmptyTranscriptError,
  unlockOrbieeVoicePlayback,
  stopOrbieeVoicePlayback,
  subscribeOrbieeVoicePlayback,
  isOrbieeVoicePlaying,
} from "../../services/websocket";
import { detectLanguageCode } from "../../utils/detectLanguage";
import ConnectToRealTimeAvatargetVideo from "./ConnectToRealTimeAvatargetVideo";
import VoiceModeControls from "./VoiceModeControls";
import VoiceModeVisual from "./VoiceModeVisual";
import VirtualTryOn from "../VirtualTryOn";
import { notifyVirtualTryOnClosed } from "../../utils/virtualTryOnAvatarHold";

// Simple broadcast-style voice icon to replace the mic glyph
const VoiceWaveIcon = ({ sx = {}, ...props }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{
      width: "0.875rem",
      height: "0.875rem",
      ...sx,
    }}
    {...props}
  >
    {/* Inner dot */}
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    {/* Middle ring */}
    <circle
      cx="12"
      cy="12"
      r="4"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    {/* Outer ring */}
    <circle
      cx="12"
      cy="12"
      r="7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeDasharray="16 8"
      fill="none"
    />
  </Box>
);

const buildChatGreetingMessage = (customerName) => {
  const name = String(customerName ?? "").trim();
  if (name) {
    const firstName = name.split(/\s+/)[0] || "there";
    return `Hi ${firstName}, I'm ${ASSISTANT_NAME}. How can I help you today?`;
  }
  return `Hi, I'm ${ASSISTANT_NAME}, please sign-in to continue.`;
};

const resolveCustomerDisplayName = (profile) => {
  if (!profile || typeof profile !== "object") return "";
  const direct = String(profile.customer_name ?? "").trim();
  if (direct) return direct;
  return deriveCustomerNameFromEmail(
    profile.username || profile.email,
    profile.customer_name
  );
};

const isLocalChatGreeting = (text, customerName) => {
  const current = String(text ?? "").trim();
  if (!current) return true;
  if (current.includes("please sign-in")) return true;
  if (current === buildChatGreetingMessage(customerName)) return true;
  if (current === buildChatGreetingMessage(null)) return true;
  return false;
};

/** Grace window before handing mic back when a voice turn produces no streamed audio. */
const NO_AUDIO_GRACE_MS = 10000;

/**
 * AvatarLogoCode Component
 * Displays the avatar with video stream, loading states, and controls
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isAvatarRecording - Whether avatar is currently recording
 * @param {Function} props.handleAvatarClick - Handler for avatar click
 * @param {MediaStream|null} props.avatarStream - WebRTC media stream
 * @param {string|null} props.avatarVideoUrl - Avatar video blob URL
 * @param {boolean} props.isAvatarMuted - Whether avatar is muted
 * @param {boolean} props.isAvatarProcessing - Whether avatar is processing
 * @param {number} props.avatarLoadingMessageIndex - Index of current loading message
 * @param {boolean} props.isAvatarConnecting - Whether avatar is connecting
 * @param {Function} props.toggleAvatarMute - Function to toggle mute state
 * @param {Function} props.forcePlayAvatarVideo - Function to force play video
 * @param {Function} props.ensureAvatarPlayback - Function to ensure video playback
 * @param {Function} props.setAvatarVideoUrl - Function to set avatar video URL
 * @param {Object} props.avatarVideoRef - Ref for video element
 * @param {Object} props.avatarCanvasRef - Ref for canvas element
 * @param {boolean} props.showControlBar - Whether to show the control bar
 * @param {Function} props.setShowControlBar - Function to set control bar visibility
 * @param {boolean} props.isListening - Whether currently listening
 * @param {Function} props.handleControlBarMicClick - Handler for microphone button click
 * @param {Function} props.handleControlBarChatClick - Handler for chat button click
 * @param {string} [props.languageCode="eng"] - Default language code for chat/API (e.g. "eng", "spa"); voice uses STT-detected language when available
 */
export default function AvatarLogoCode({
  isAvatarRecording = false,
  handleAvatarClick,
  avatarStream = null,
  avatarVideoUrl = null,
  isAvatarMuted = false,
  isAvatarProcessing = false,
  avatarLoadingMessageIndex = 0,
  isAvatarConnecting = false,
  toggleAvatarMute,
  forcePlayAvatarVideo,
  ensureAvatarPlayback,
  setAvatarVideoUrl,
  avatarVideoRef,
  avatarCanvasRef,
  showControlBar: externalShowControlBar,
  setShowControlBar: externalSetShowControlBar,
  isListening = false,
  handleControlBarMicClick,
  handleControlBarChatClick,
  languageCode = "eng",
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, replaceCartItemsFromAgent } = useCart();
  const wishlist = useWishlist();
  const [wishlistToast, setWishlistToast] = useState(null);

  // State to track if avatar video is loading after login
  const [isAvatarVideoLoading, setIsAvatarVideoLoading] = useState(false);

  // Internal state for showControlBar if not provided as prop
  const [internalShowControlBar, setInternalShowControlBar] = useState(false);

  // Use external state if provided, otherwise use internal state
  const showControlBar =
    externalShowControlBar !== undefined
      ? externalShowControlBar
      : internalShowControlBar;
  const setShowControlBar =
    externalSetShowControlBar || setInternalShowControlBar;

  // Internal state for chat box
  // Check sessionStorage on mount to restore chat state after navigation
  const [isChatOpen, setIsChatOpen] = useState(() => {
    // Check if user manually closed the chat
    const wasManuallyClosed =
      sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

    // If manually closed, don't auto-open
    if (wasManuallyClosed) {
      return false;
    }

    // Check if chat should be open after navigation
    const shouldBeOpen =
      sessionStorage.getItem("orbiee_chat_should_be_open") === "true";
    if (shouldBeOpen) {
      // Clear the flag after reading
      sessionStorage.removeItem("orbiee_chat_should_be_open");
      return true;
    }
    return false;
  });

  // Initialize chat messages with greeting based on login status
  const getInitialMessage = () => {
    const profile = getUserProfile();
    const displayMessage = buildChatGreetingMessage(
      resolveCustomerDisplayName(profile)
    );
    return [
      {
        id: 1,
        conversation_text: displayMessage,
        answer: displayMessage, // Support both conversation_text and answer
        isUser: false,
        timestamp: new Date(),
      },
    ];
  };

  const [chatMessages, setChatMessages] = useState(getInitialMessage());
  const [chatInputValue, setChatInputValue] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);

  const applySignedInGreeting = useCallback((customerName) => {
    const name = String(customerName ?? "").trim();
    if (!name) return;

    const newGreeting = buildChatGreetingMessage(name);
    const guestGreeting = buildChatGreetingMessage(null);

    setChatMessages((prev) => {
      if (!prev.length) return prev;
      const firstMessage = prev[0];
      const currentMessage =
        firstMessage.conversation_text || firstMessage.answer || "";
      const backendWelcome = getOrbieeBackendWelcomeMessage();
      if (backendWelcome && currentMessage === backendWelcome) return prev;
      if (currentMessage === newGreeting) return prev;
      if (
        currentMessage !== guestGreeting &&
        !currentMessage.includes("please sign-in")
      ) {
        return prev;
      }
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        conversation_text: newGreeting,
        answer: newGreeting,
      };
      return updated;
    });
  }, []);

  const applyBackendWelcomeToChat = useCallback((backendText) => {
    const text = String(backendText ?? "").trim();
    if (!text) return;

    const name =
      resolveCustomerDisplayName(user) ||
      resolveCustomerDisplayName(getUserProfile());

    setChatMessages((prev) => {
      if (!prev.length) {
        return [
          {
            id: 1,
            conversation_text: text,
            answer: text,
            isUser: false,
            timestamp: new Date(),
          },
        ];
      }

      const firstMessage = prev[0];
      if (firstMessage.isUser) return prev;

      const currentMessage = String(
        firstMessage.conversation_text || firstMessage.answer || ""
      ).trim();
      if (currentMessage === text) return prev;
      if (!isLocalChatGreeting(currentMessage, name)) return prev;

      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        conversation_text: text,
        answer: text,
      };
      return updated;
    });
  }, [user]);

  // Prefer the backend connect-greeting when it arrives; otherwise keep hardcoded.
  useEffect(() => {
    return subscribeOrbieeBackendWelcome(applyBackendWelcomeToChat);
  }, [applyBackendWelcomeToChat]);

  // Update greeting when auth user becomes available (login or page refresh)
  useEffect(() => {
    const name =
      resolveCustomerDisplayName(user) ||
      resolveCustomerDisplayName(getUserProfile());
    if (name) applySignedInGreeting(name);
  }, [user, applySignedInGreeting]);

  // AuthContext dispatches "authStateChanged" (not "authStateChange")
  useEffect(() => {
    const handleAuthChange = (event) => {
      const name =
        resolveCustomerDisplayName(event?.detail) ||
        resolveCustomerDisplayName(getUserProfile());
      if (name) {
        setTimeout(() => applySignedInGreeting(name), 100);
      }
    };

    window.addEventListener("authStateChanged", handleAuthChange);
    window.addEventListener("authStateChange", handleAuthChange);
    return () => {
      window.removeEventListener("authStateChanged", handleAuthChange);
      window.removeEventListener("authStateChange", handleAuthChange);
    };
  }, [applySignedInGreeting]);

  // Listen for background chat events (from realtime avatar) and append to chat
  useEffect(() => {
    const handleBackgroundChat = (event) => {
      const { userText, botText, botTexts } = event.detail || {};
      const newMessages = [];
      const baseId = Date.now();

      if (userText) {
        newMessages.push({
          id: baseId,
          content: userText,
          isUser: true,
          timestamp: new Date(),
        });
      }

      const assistantTexts = Array.isArray(botTexts) && botTexts.length
        ? botTexts.map((t) => String(t ?? "").trim()).filter(Boolean)
        : botText
          ? [String(botText).trim()]
          : [];

      assistantTexts.forEach((text, i) => {
        newMessages.push({
          id: `${baseId}-bot-${i}`,
          conversation_text: text,
          answer: text,
          isUser: false,
          isLlmSegment: true,
          timestamp: new Date(),
        });
      });

      if (newMessages.length) {
        setChatMessages((prev) => [...prev, ...newMessages]);
      }
    };

    window.addEventListener("orbieeBackgroundChat", handleBackgroundChat);
    return () =>
      window.removeEventListener("orbieeBackgroundChat", handleBackgroundChat);
  }, []);

  // Voice mode state
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  /** True while streamed assistant reply audio is playing (voice visual "speaking"). */
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const recordingRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const isVoiceModeRef = useRef(false); // Track voice mode state to avoid stale closures
  const isRecordingRef = useRef(false);
  const isChatLoadingRef = useRef(false);
  const isMicMutedRef = useRef(false);
  const voiceRestartTimerRef = useRef(null);
  const startVoiceModeRef = useRef(null);
  const activeStreamIdRef = useRef(null);

  useEffect(() => {
    if (!wishlistToast) return undefined;
    const t = setTimeout(() => setWishlistToast(null), 2500);
    return () => clearTimeout(t);
  }, [wishlistToast]);

  // Virtual Try-On state
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnProductId, setTryOnProductId] = useState(null);


  // Track chat state in sessionStorage whenever it changes
  useEffect(() => {
    // Store current chat state in sessionStorage so OrbieeFlow can check it
    if (isChatOpen) {
      sessionStorage.setItem("orbiee_chat_is_open", "true");
      // Only clear manually closed flag if chat is explicitly opened (not just state change)
      // We'll handle clearing it in handleChatClick and startVoiceMode instead
    } else {
      sessionStorage.removeItem("orbiee_chat_is_open");
      // Don't set manually closed flag here - it's set explicitly in onClose handler
    }
  }, [isChatOpen]);

  // Adjust body padding when chat is open to create split-screen effect
  useEffect(() => {
    if (isChatOpen) {
      // Only add padding on desktop (sm and up) - mobile is full screen overlay
      const isMobile = window.innerWidth < 600;
      if (!isMobile) {
        const chatWidth = 400;
        document.body.style.paddingRight = `${chatWidth}px`;
        document.body.style.transition = "padding-right 0.3s ease";
      } else {
        // Prevent body scroll on mobile when chat is open
        document.body.style.overflow = "hidden";
      }
    } else {
      document.body.style.paddingRight = "";
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.paddingRight = "";
      document.body.style.overflow = "";
    };
  }, [isChatOpen]);

  // Check sessionStorage for virtual try-on trigger
  useEffect(() => {
    const checkVirtualTryOn = () => {
      const tryOnOpenFlag = sessionStorage.getItem("orbiee_virtual_try_on_open");
      const productId = sessionStorage.getItem(
        "orbiee_virtual_try_on_product_id"
      );

      // Check if we should open the modal
      if (tryOnOpenFlag === "true" && productId) {
        setTryOnProductId(productId);
        setTryOnOpen(true);
        // Clear the flags after reading
        sessionStorage.removeItem("orbiee_virtual_try_on_open");
        sessionStorage.removeItem("orbiee_virtual_try_on_product_id");
      }
      // Check if we should close the modal
      else if (tryOnOpenFlag === "false") {
        setTryOnOpen(false);
        setTryOnProductId(null);
        notifyVirtualTryOnClosed();
        // Clear the flags after reading
        sessionStorage.removeItem("orbiee_virtual_try_on_open");
        sessionStorage.removeItem("orbiee_virtual_try_on_product_id");
        sessionStorage.removeItem("orbiee_virtual_try_on_autoclick_profile");
      }
    };

    // Check immediately
    checkVirtualTryOn();

    // Also check periodically in case the flag was set before this component mounted
    const interval = setInterval(checkVirtualTryOn, 500);

    return () => clearInterval(interval);
  }, []);

  // Auto-click "Try on with your profile picture" after the modal opens when flagged.
  useEffect(() => {
    if (!tryOnOpen) return;

    const shouldAutoclick =
      sessionStorage.getItem("orbiee_virtual_try_on_autoclick_profile") ===
      "true";
    if (!shouldAutoclick) return;

    const tryClick = () => {
      const buttons = Array.from(document.querySelectorAll("button"));
      const target = buttons.find(
        (btn) =>
          btn.textContent &&
          btn.textContent
            .toLowerCase()
            .includes("try on with your profile picture")
      );
      if (target) {
        target.click();
        sessionStorage.removeItem("orbiee_virtual_try_on_autoclick_profile");
        return true;
      }
      return false;
    };

    // Attempt immediately; if not ready, retry briefly.
    if (tryClick()) return;
    const interval = setInterval(() => {
      if (tryClick()) {
        clearInterval(interval);
      }
    }, 300);
    // Safety timeout to stop retries
    const timeout = setTimeout(() => clearInterval(interval), 4000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [tryOnOpen]);

  // Handle chat button click
  const handleChatClick = () => {
    if (handleControlBarChatClick) {
      handleControlBarChatClick();
    } else {
      // Default behavior: open chat box
      setIsChatOpen(true);
      setShowControlBar(false);
      // Clear manual close flag when user opens chat
      sessionStorage.removeItem("orbiee_chat_manually_closed");
    }
  };

  /** Replace in-flight LLM segment bubbles for the current turn (one box per llm_response). */
  const applyLlmDisplaySegments = useCallback((segments, streamId) => {
    if (!streamId || !Array.isArray(segments)) return false;
    const texts = segments
      .map((text) => String(text ?? "").trim())
      .filter(Boolean);
    if (!texts.length) return false;

    const prefix = `llm-${streamId}-`;
    setChatMessages((prev) => {
      const kept = prev.filter((m) => !String(m.id).startsWith(prefix));
      const bubbles = texts.map((text, i) => ({
        id: `${prefix}${i}`,
        conversation_text: text,
        answer: text,
        isUser: false,
        isLlmSegment: true,
        timestamp: new Date(),
      }));
      return [...kept, ...bubbles];
    });
    return true;
  }, []);

  // Handle sending message (typed text or voice via WebSocket `voice` frame)
  // sendOptions.audioBlob: WAV blob — STT + agent turn on backend
  // sendOptions.languageCode: optional override when transcript returns language_code
  const handleSendMessage = useCallback(
    async (textToSend = null, sendOptions = {}) => {
      const voiceBlob =
        sendOptions.audioBlob instanceof Blob ||
        sendOptions.audioBlob instanceof File
          ? sendOptions.audioBlob
          : null;
      const messageText = voiceBlob ? "" : textToSend || chatInputValue.trim();
      if ((!voiceBlob && !messageText) || isChatLoading) return;

      const langForApi = voiceBlob
        ? sendOptions.languageCode ?? languageCode
        : sendOptions.languageCode ??
          detectLanguageCode(messageText) ??
          languageCode;

      const streamId = Date.now();
      activeStreamIdRef.current = streamId;
      let displayedLlmSegments = false;
      let userMessageAdded = false;

      const appendUserTranscript = (text) => {
        if (!isValidTranscription(text) || userMessageAdded) return;
        userMessageAdded = true;
        setChatMessages((prev) => [
          ...prev,
          {
            id: streamId,
            content: text,
            isUser: true,
            timestamp: new Date(),
          },
        ]);
      };

      // Typed chat: show user bubble immediately
      if (!voiceBlob) {
        setChatMessages((prev) => [
          ...prev,
          {
            id: streamId,
            content: messageText,
            isUser: true,
            timestamp: new Date(),
          },
        ]);
        userMessageAdded = true;
        if (!textToSend) {
          setChatInputValue("");
        }
      }

      setIsChatLoading(true);

      const onLlmDisplayUpdate = (segments) => {
        if (applyLlmDisplaySegments(segments, streamId)) {
          displayedLlmSegments = true;
        }
      };

      const flowPayload = voiceBlob
        ? { audioBlob: voiceBlob }
        : { content: messageText };

      try {
        const userProfile = getUserProfile();
        const customerId = userProfile?.customer_id;

        const conversationText = await OrbieeFlow(
          flowPayload,
          customerId,
          navigate,
          addToCart,
          langForApi,
          (lastResponse) => {
            // Agent cart snapshot → UI (no Cart API)
            applyCartFromAgentResponse(
              replaceCartItemsFromAgent,
              lastResponse
            );
            const bubbles = buildChatBubbleTextsFromResponse(lastResponse);
            if (applyLlmDisplaySegments(bubbles, streamId)) {
              displayedLlmSegments = true;
            }
            const { toast } = applyWishlistFromAgentResponse(
              lastResponse,
              wishlist,
              { navigate }
            );
            if (toast) setWishlistToast(toast);
            if (voiceBlob) {
              appendUserTranscript(
                lastResponse?.user_transcription || lastResponse?.transcription
              );
            }
          },
          {
            // Chat-box surfaces only: typed → text, mic → voice (avatar widget sets mode:"avatar")
            mode: voiceBlob ? "voice" : "text",
            onLlmDisplayUpdate,
            languageCode: langForApi,
            onTranscript: voiceBlob
              ? ({ text }) => {
                  appendUserTranscript(text);
                }
              : undefined,
          }
        );

        if (voiceBlob && !userMessageAdded) {
          const errorMessage = {
            id: streamId,
            conversation_text:
              "Sorry, I couldn't understand your voice. Please try again.",
            answer:
              "Sorry, I couldn't understand your voice. Please try again.",
            isUser: false,
            timestamp: new Date(),
          };
          setChatMessages((prev) => [...prev, errorMessage]);
        } else if (!displayedLlmSegments) {
          if (conversationText) {
            const botMessage = {
              id: `llm-${streamId}-0`,
              conversation_text: conversationText,
              answer: conversationText,
              isUser: false,
              timestamp: new Date(),
            };
            setChatMessages((prev) => [...prev, botMessage]);
          } else {
            const botMessage = {
              id: Date.now() + 1,
              conversation_text:
                "I'm sorry, I couldn't process your message. Please make sure you're logged in.",
              answer:
                "I'm sorry, I couldn't process your message. Please make sure you're logged in.",
              isUser: false,
              timestamp: new Date(),
            };
            setChatMessages((prev) => [...prev, botMessage]);
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        if (activeStreamIdRef.current === streamId) {
          const prefix = `llm-${streamId}-`;
          setChatMessages((prev) =>
            prev.filter((m) => !String(m.id).startsWith(prefix))
          );
        }
        // Mic closed with silence / STT heard nothing — don't poison the chat UI.
        if (isEmptyTranscriptError(error)) {
          setTimeout(() => {
            if (
              isVoiceModeRef.current &&
              !isMicMutedRef.current &&
              !isRecordingRef.current &&
              typeof startVoiceModeRef.current === "function"
            ) {
              startVoiceModeRef.current();
            }
          }, 400);
          return;
        }
        const botMessage = {
          id: Date.now() + 1,
          conversation_text:
            "Sorry, there was an error processing your message. Please try again.",
          answer:
            "Sorry, there was an error processing your message. Please try again.",
          isUser: false,
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, botMessage]);
      } finally {
        if (activeStreamIdRef.current === streamId) {
          activeStreamIdRef.current = null;
        }
        setIsChatLoading(false);
      }
    },
    [
      addToCart,
      applyLlmDisplaySegments,
      chatInputValue,
      isChatLoading,
      languageCode,
      navigate,
      wishlist,
      replaceCartItemsFromAgent,
    ]
  );

  // Handle key press in chat input
  const handleChatKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && chatInputValue.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Process recording result (extracted to be reusable for automatic silence/timeout stops)
  const processRecordingResult = useCallback(
    async (result) => {
      if (!result) {
        return;
      }

      // Only process if we have audio buffers (don't process empty/muted recordings)
      if (
        result &&
        result.buffers &&
        result.buffers.length > 0 &&
        result.audioContext
      ) {
        try {
          const wavBlob = await convertBuffersToWAV(
            result.buffers,
            result.audioContext.sampleRate || 44100
          );

          await handleSendMessage(null, { audioBlob: wavBlob });
        } catch (transcribeError) {
          console.error("Voice message error:", transcribeError);
          setIsChatLoading(false);
          if (isEmptyTranscriptError(transcribeError)) {
            setTimeout(() => {
              if (
                isVoiceModeRef.current &&
                !isMicMutedRef.current &&
                !isRecordingRef.current &&
                typeof startVoiceModeRef.current === "function"
              ) {
                startVoiceModeRef.current();
              }
            }, 400);
            return;
          }
          const errorMessage = {
            id: Date.now() + 1,
            conversation_text:
              "Sorry, I couldn't understand your voice. Please try again.",
            answer: "Sorry, I couldn't understand your voice. Please try again.",
            isUser: false,
            timestamp: new Date(),
          };
          setChatMessages((prev) => [...prev, errorMessage]);
        }
      } else {
        setIsChatLoading(false);
      }
    },
    [handleSendMessage]
  );

  const clearVoiceRestartTimer = useCallback(() => {
    if (voiceRestartTimerRef.current) {
      clearTimeout(voiceRestartTimerRef.current);
      voiceRestartTimerRef.current = null;
    }
  }, []);

  const cleanupMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Schedule mic re-open while still in voice conversation mode
  const scheduleVoiceRestart = useCallback(
    (delayMs = 400) => {
      clearVoiceRestartTimer();
      voiceRestartTimerRef.current = setTimeout(() => {
        voiceRestartTimerRef.current = null;
        // Muted = pause the listen loop; user must unmute to record again.
        if (isMicMutedRef.current) return;
        if (
          isVoiceModeRef.current &&
          !isRecordingRef.current &&
          !isChatLoadingRef.current &&
          !recordingRef.current
        ) {
          if (typeof startVoiceModeRef.current === "function") {
            startVoiceModeRef.current();
          }
        }
      }, delayMs);
    },
    [clearVoiceRestartTimer]
  );

  const voicePlaybackActiveRef = useRef(false);
  const audioGraceTimerRef = useRef(null);

  const clearAudioGraceTimer = useCallback(() => {
    if (audioGraceTimerRef.current != null) {
      clearTimeout(audioGraceTimerRef.current);
      audioGraceTimerRef.current = null;
    }
  }, []);

  // Start voice recording
  const startVoiceMode = useCallback(async () => {
    // Prevent overlapping recordings
    if (recordingRef.current || isRecordingRef.current) {
      return;
    }

    try {
      clearVoiceRestartTimer();

      // Unmute whenever the user intentionally starts a listen loop.
      setIsMicMuted(false);
      isMicMutedRef.current = false;

      // The mic tap is the user gesture that lets the browser play the
      // streamed voice reply for this turn. Safe to call repeatedly.
      unlockOrbieeVoicePlayback();

      // Activate voice mode (controls render below avatar; chat stays optional)
      setIsVoiceMode(true);
      isVoiceModeRef.current = true;
      setIsRecording(true);
      isRecordingRef.current = true;

      // Start recording
      const recording = await startVoiceRecording(
        (rms) => {
          // Update audio level for visualization
          setAudioLevel(rms);
        },
        () => {
          // Speech detected callback
        },
        () => isMicMutedRef.current, // Live mute check (avoid stale closure)
        async (result) => {
          // Automatic stop callback (silence/timeout detected)

          // Only process if we have recorded buffers (not muted recording)
          if (!result || !result.buffers || result.buffers.length === 0) {
            recordingRef.current = null;
            setIsRecording(false);
            isRecordingRef.current = false;
            setAudioLevel(0);
            cleanupMediaStream();
            // Keep conversation loop alive — listen again (unless muted)
            if (isVoiceModeRef.current && !isMicMutedRef.current) {
              scheduleVoiceRestart(400);
            }
            return;
          }

          // Clear ref and state
          recordingRef.current = null;
          setIsRecording(false);
          isRecordingRef.current = false;
          // Process the audio and call API
          await processRecordingResult(result);
          // Cleanup
          cleanupMediaStream();
          // Don't set isVoiceMode to false here - keep it active for re-activation
          setAudioLevel(0);
        }
      );

      // Voice mode may have been cancelled / muted while awaiting getUserMedia
      if (!isVoiceModeRef.current || isMicMutedRef.current) {
        try {
          recording.stopRecording();
        } catch (_) {
          /* ignore */
        }
        setIsRecording(false);
        isRecordingRef.current = false;
        cleanupMediaStream();
        return;
      }

      recordingRef.current = recording;
      mediaStreamRef.current = recording.mediaStream;
    } catch (error) {
      console.error("Error starting voice recording:", error);
      alert("Failed to access microphone. Please check permissions.");
      setIsVoiceMode(false);
      isVoiceModeRef.current = false;
      setIsRecording(false);
      isRecordingRef.current = false;
    }
  }, [
    processRecordingResult,
    clearVoiceRestartTimer,
    cleanupMediaStream,
    scheduleVoiceRestart,
  ]);

  startVoiceModeRef.current = startVoiceMode;

  /** Stop capture and discard buffers — stay in voice mode, send nothing. */
  const discardActiveCapture = useCallback(() => {
    clearVoiceRestartTimer();

    const recording = recordingRef.current;
    recordingRef.current = null;

    if (recording) {
      try {
        recording.stopRecording();
      } catch (error) {
        console.error("Error discarding capture:", error);
      }
    }

    cleanupMediaStream();
    setIsRecording(false);
    isRecordingRef.current = false;
    setAudioLevel(0);
  }, [clearVoiceRestartTimer, cleanupMediaStream]);

  // Red stop button / end conversation: discard audio, send nothing, leave voice mode
  const cancelVoiceRecording = useCallback(() => {
    clearVoiceRestartTimer();
    // Silence any reply still being spoken — the shopper ended the turn.
    stopOrbieeVoicePlayback();

    setIsVoiceMode(false);
    isVoiceModeRef.current = false;
    setIsMicMuted(false);
    isMicMutedRef.current = false;
    setIsRecording(false);
    isRecordingRef.current = false;
    setAudioLevel(0);
    setIsAssistantSpeaking(false);

    const recording = recordingRef.current;
    recordingRef.current = null;

    if (recording) {
      try {
        // Stop capture only — do not process or send to backend
        recording.stopRecording();
      } catch (error) {
        console.error("Error cancelling recording:", error);
      }
    }

    cleanupMediaStream();
  }, [clearVoiceRestartTimer, cleanupMediaStream]);

  /**
   * Mute: stop recording immediately, discard audio, stay in voice mode.
   * Unmute: start a fresh listen loop (nothing from the muted period is sent).
   */
  const toggleMicMute = useCallback(() => {
    if (isMicMutedRef.current) {
      setIsMicMuted(false);
      isMicMutedRef.current = false;
      if (
        isVoiceModeRef.current &&
        !isRecordingRef.current &&
        !isChatLoadingRef.current
      ) {
        startVoiceMode();
      }
      return;
    }

    setIsMicMuted(true);
    isMicMutedRef.current = true;
    discardActiveCapture();
  }, [discardActiveCapture, startVoiceMode]);

  // Handle voice mode button click
  const handleVoiceModeClick = useCallback(() => {
    if (isRecordingRef.current || recordingRef.current) {
      // End conversation — discard in-progress audio
      cancelVoiceRecording();
    } else {
      startVoiceMode();
    }
  }, [cancelVoiceRecording, startVoiceMode]);

  // Combined handler for microphone button - ensures voice mode is activated
  const handleMicButtonClick = useCallback(() => {
    // Always call the internal handler first to ensure voice mode activates reliably
    handleVoiceModeClick();

    // If external handler is also provided, call it (for any additional functionality)
    if (handleControlBarMicClick && typeof handleControlBarMicClick === 'function') {
      try {
        handleControlBarMicClick();
      } catch (error) {
        console.error("Error in handleControlBarMicClick:", error);
      }
    }
  }, [handleControlBarMicClick, handleVoiceModeClick]);

  // Sync refs with state (avoid stale closures in TTS / restart timers)
  useEffect(() => {
    isVoiceModeRef.current = isVoiceMode;
  }, [isVoiceMode]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isChatLoadingRef.current = isChatLoading;
  }, [isChatLoading]);

  useEffect(() => {
    isMicMutedRef.current = isMicMuted;
  }, [isMicMuted]);

  // Assistant finished speaking its streamed reply — reopen the mic if still in voice mode.
  const handleTTSFinished = useCallback(() => {
    if (isMicMutedRef.current) return;
    if (
      isVoiceModeRef.current &&
      !isRecordingRef.current &&
      !isChatLoadingRef.current
    ) {
      scheduleVoiceRestart(500);
    }
  }, [scheduleVoiceRestart]);

  const maybeNotifyVoiceTurnComplete = useCallback(() => {
    if (!isVoiceModeRef.current) return;
    if (isChatLoadingRef.current) return;
    if (voicePlaybackActiveRef.current) return;
    clearAudioGraceTimer();
    handleTTSFinished();
  }, [clearAudioGraceTimer, handleTTSFinished]);

  // Streamed voice reply playback → voice-mode mic hand-back (works with chat closed).
  useEffect(() => {
    const unsubscribe = subscribeOrbieeVoicePlayback((state) => {
      if (state === "playing") {
        voicePlaybackActiveRef.current = true;
        setIsAssistantSpeaking(true);
        clearAudioGraceTimer();
        return;
      }
      voicePlaybackActiveRef.current = false;
      setIsAssistantSpeaking(false);
      maybeNotifyVoiceTurnComplete();
    });
    return unsubscribe;
  }, [clearAudioGraceTimer, maybeNotifyVoiceTurnComplete]);

  // Turn finished with no streamed audio — hand mic back after grace window.
  useEffect(() => {
    if (!isVoiceMode || isChatLoading) {
      clearAudioGraceTimer();
      return undefined;
    }

    if (voicePlaybackActiveRef.current || isOrbieeVoicePlaying()) {
      return undefined;
    }

    clearAudioGraceTimer();
    audioGraceTimerRef.current = setTimeout(() => {
      audioGraceTimerRef.current = null;
      if (voicePlaybackActiveRef.current || isOrbieeVoicePlaying()) return;
      maybeNotifyVoiceTurnComplete();
    }, NO_AUDIO_GRACE_MS);

    return clearAudioGraceTimer;
  }, [
    isVoiceMode,
    isChatLoading,
    clearAudioGraceTimer,
    maybeNotifyVoiceTurnComplete,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearVoiceRestartTimer();
      if (recordingRef.current) {
        try {
          recordingRef.current.stopRecording();
        } catch (_) {
          /* ignore */
        }
        recordingRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
    };
  }, [clearVoiceRestartTimer]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 12, sm: 16 },
        right: { xs: 12, sm: 16 },
        zIndex: 40,
        width: { xs: 140, sm: 192 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Avatar — fixed size; voice controls live in a sibling row below */}
      <Box
        sx={{
          width: "100%",
          borderRadius: 4, // rounded-2xl
          overflow: "hidden",
          cursor: "pointer",
          mb: 0,
          position: "relative",
          transition: "all 0.3s ease",
          height: "30vh",
          // ✅ ADD THIS LINE to make background transparent:
          backgroundColor: "transparent",
          // If there's already a backgroundColor property, change it to "transparent"

          border:
            !isVoiceMode && isAvatarRecording
              ? `4px solid ${alpha("#ef4444", 0.75)}`
              : "none",
          boxShadow:
            !isVoiceMode && isAvatarRecording
              ? `0 0 0 4px ${alpha("#ef4444", 0.75)}`
              : "none",
          animation:
            !isVoiceMode && isAvatarRecording
              ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
              : "none",
          "&:hover": {
            border:
              !isVoiceMode && !isAvatarRecording
                ? `2px solid ${alpha("#93c5fd", 1)}`
                : undefined,
            boxShadow:
              !isVoiceMode && !isAvatarRecording
                ? `0 0 0 2px ${alpha("#93c5fd", 1)}`
                : undefined,
          },
          cursor: isVoiceMode ? "default" : "pointer",
          "@keyframes pulse": {
            "0%, 100%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.5,
            },
          },
        }}
        onClick={isVoiceMode ? undefined : handleAvatarClick}
        title={
          isVoiceMode
            ? undefined
            : isAvatarRecording
              ? "Click to stop"
              : "Click to talk to Orbiee"
        }
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
          }}
        >
          {/* Keep avatar mounted (idle/WebRTC unchanged); only hide visually in voice mode */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              visibility: isVoiceMode ? "hidden" : "visible",
              pointerEvents: isVoiceMode ? "none" : "auto",
            }}
            aria-hidden={isVoiceMode}
          >
            <ConnectToRealTimeAvatargetVideo
              onAvatarVideoLoadingChange={setIsAvatarVideoLoading}
            />
          </Box>

          {isVoiceMode && (
            <VoiceModeVisual
              audioLevel={audioLevel}
              isRecording={isRecording}
              isLoading={isChatLoading}
              isMicMuted={isMicMuted}
              isSpeaking={isAssistantSpeaking}
            />
          )}
        </Box>
      </Box>

      {/* Voice mode controls — below avatar, outside chat box */}
      {isVoiceMode && (
        <VoiceModeControls
          isRecording={isRecording}
          isMicMuted={isMicMuted}
          isLoading={isChatLoading}
          onToggleMicMute={toggleMicMute}
          onStopRecording={cancelVoiceRecording}
          onStartVoiceMode={startVoiceMode}
        />
      )}

      {/* Talk to Orbiee Button - Only show when control bar is not visible and video is loaded */}
      {!showControlBar && !isAvatarVideoLoading && (
        <Button
          onClick={() => {
            // Allow opening control bar even when not logged in
            if (setShowControlBar) {
              setShowControlBar(true);
            }
          }}
          sx={{
            width: "100%",
            px: { xs: 1.5, sm: 2 }, // px-4
            py: { xs: 0.75, sm: 1 }, // py-2
            borderRadius: { xs: 3, sm: 4 }, // rounded-2xl
            color: "white",
            fontWeight: 500,
            fontSize: { xs: "0.8125rem", sm: "0.875rem" }, // text-sm
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // shadow-lg
            background: "linear-gradient(to right, #22d3ee, #06b6d4, #3b82f6)", // from-cyan-400 via-cyan-500 to-blue-500
            textTransform: "none", // Remove default uppercase
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // hover:shadow-xl
              transform: { xs: "scale(1.02)", sm: "scale(1.05)" }, // hover:scale-105
              background:
                "linear-gradient(to right, #22d3ee, #06b6d4, #3b82f6)",
            },
            "&:active": {
              transform: "scale(1.01)",
            },
          }}
        >
          Talk to Orbiee
        </Button>
      )}

      {/* Control Bar — hidden while voice mode is active so the avatar does not shift */}
      {showControlBar && !isVoiceMode && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 0,
            position: "relative",
          }}
        >
          {/* Control Buttons Container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              background:
                "linear-gradient(to right, #22d3ee, #06b6d4, #3b82f6)", // from-cyan-400 via-cyan-500 to-blue-500
              borderRadius: "9999px", // rounded-full
              px: { xs: 1, sm: 1.5 }, // px-3
              py: { xs: 0.75, sm: 1 }, // py-2
              boxShadow: "0 12px 28px -8px rgba(59, 130, 246, 0.55)", // shadow-[0_12px_28px_-8px_rgba(59,130,246,0.55)]
            }}
          >
            {/* Back Button */}
            <IconButton
              onClick={() => {
                if (setShowControlBar) {
                  setShowControlBar(false);
                }
              }}
              sx={{
                width: { xs: 36, sm: 40 }, // w-10
                height: { xs: 36, sm: 40 }, // h-10
                backgroundColor: "white",
                borderRadius: "50%",
                color: "#2563eb", // text-blue-600
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // shadow-lg
                "&:hover": {
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // hover:shadow-xl
                },
                "&:active": {
                  transform: "scale(0.95)", // active:scale-95
                },
                transition: "all 0.2s ease",
              }}
              aria-label="Back"
              title="Back"
            >
              <ChevronLeft style={{ width: 16, height: 16 }} />
            </IconButton>

            {/* Microphone Button */}
            <IconButton
              onClick={handleMicButtonClick}
              sx={{
                width: { xs: 36, sm: 40 }, // w-10
                height: { xs: 36, sm: 40 }, // h-10
                borderRadius: "50%",
                backgroundColor:
                  isAvatarRecording || isListening || isVoiceMode || isRecording
                    ? "#ef4444" // bg-red-500
                    : "#d1fae5", // bg-emerald-100
                color:
                  isAvatarRecording || isListening || isVoiceMode || isRecording
                    ? "white"
                    : "#059669", // text-emerald-600
                border:
                  isAvatarRecording || isListening || isVoiceMode || isRecording
                    ? `4px solid ${alpha("#fecaca", 0.8)}` // ring-4 ring-red-200
                    : "none",
                boxShadow:
                  isAvatarRecording || isListening || isVoiceMode || isRecording
                    ? `0 0 0 4px ${alpha(
                      "#fecaca",
                      0.8
                    )}, 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)`
                    : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                "&:hover": {
                  boxShadow:
                    isAvatarRecording || isListening || isVoiceMode || isRecording
                      ? `0 0 0 4px ${alpha(
                        "#fecaca",
                        0.8
                      )}, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)`
                      : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // hover:shadow-xl
                },
                "&:active": {
                  transform: "scale(0.95)", // active:scale-95
                },
                transition: "all 0.2s ease",
              }}
              aria-label="Voice mode"
              title="Start voice conversation"
            >
              <VoiceWaveIcon />
            </IconButton>

            {/* Chat Button */}
            <IconButton
              onClick={handleChatClick}
              sx={{
                width: 40, // w-10
                height: 40, // h-10
                backgroundColor: "white",
                borderRadius: "50%",
                color: "#2563eb", // text-blue-600
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // shadow-lg
                "&:hover": {
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // hover:shadow-xl
                },
                "&:active": {
                  transform: "scale(0.95)", // active:scale-95
                },
                transition: "all 0.2s ease",
              }}
              aria-label="Open chat"
              title="Open chat"
            >
              <MessageCircle style={{ width: 14, height: 14 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Chat Box */}
      <ChartBoxCode
        isOpen={isChatOpen}
        messages={chatMessages}
        inputValue={chatInputValue}
        setInputValue={setChatInputValue}
        onClose={() => {
          sessionStorage.setItem("orbiee_chat_manually_closed", "true");
          setIsChatOpen(false);
        }}
        onSendMessage={handleSendMessage}
        onKeyPress={handleChatKeyPress}
        isCartCollapsed={true}
        setIsCartCollapsed={() => { }}
        totalItems={0}
        onSuggestionClick={(answer) => {
          // Handle suggestion click
          setChatInputValue(answer);
          setTimeout(() => handleSendMessage(), 100);
        }}
        isLoading={isChatLoading}
        listenAvatarEvents={false}
      />

      {wishlistToast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 88,
            right: 24,
            zIndex: 1400,
            bgcolor: "rgba(17, 24, 39, 0.92)",
            color: "white",
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: "0.875rem",
            boxShadow: 3,
            pointerEvents: "none",
          }}
        >
          {wishlistToast}
        </Box>
      )}

      {/* Virtual Try-On Modal */}
      <VirtualTryOn
        open={tryOnOpen}
        onClose={() => {
          notifyVirtualTryOnClosed();
          setTryOnOpen(false);
          setTryOnProductId(null);
        }}
        productImage=""
        article_id={tryOnProductId}
      />
    </Box>
  );
}
